/* Product gallery — the behaviour of the gallery of the product page.
 *
 * Why a file of its own rather than a {% javascript %} block in the snippet:
 * `shopify theme dev` serves the compiled bundles truncated past a threshold
 * that moves from one run to the next, and a bundle that does not parse
 * registers nothing at all — one component too many and every other component
 * of the theme stops being defined in development. Files in `assets/` are
 * served whole. Same reasoning, and same shape, as `cart.js`.
 *
 * Roles, as for the cart: the Liquid file (`snippets/product-gallery.liquid`)
 * carries the markup, the `data-*` attributes read here and the CSS; this file
 * carries the behaviour alone and knows nothing of the shop or of the design.
 *
 * What the component is for. The navigation itself is the browser's: the
 * thumbnails are fragment links and the mobile stack is a scroller, both of
 * which work with this file absent — which is the point, and why the arrows
 * ship hidden rather than dead. What the component adds is the state: which
 * thumbnail is current, whether there is another picture to step to, and what a
 * screen reader is told once the image on screen has changed.
 *
 * Markup contract (what the script looks for in the DOM):
 *
 *   <product-gallery> containing:
 *     [data-gallery-stack]   the scroller holding the slides; takes
 *                            data-gallery-snap once the page has loaded,
 *                            which is what turns its snapping on (WebKit
 *                            would otherwise re-snap it to another slide
 *                            while the first picture arrives)
 *     [data-gallery-slide]   one per image, in order, each carrying
 *                            data-gallery-label — the sentence the live
 *                            region repeats when that image is reached,
 *                            translated in Liquid so that no string of the
 *                            gallery is built here
 *     [data-gallery-thumb]   one per image, in the same order; takes
 *                            aria-current="true" when its image is on screen
 *     [data-gallery-group]   optional, on a slide and on the row holding a
 *                            thumbnail: the model that picture belongs to,
 *                            empty for a packshot. One group shows at a time,
 *                            the packshots always; the server ships the others
 *                            `hidden` and this file moves that around on
 *                            `product:model` (see product-models.js)
 *     [data-gallery-jump]    optional, any number, anywhere: a fragment link
 *                            to one of the slides, which glides like a
 *                            thumbnail without being one — no position in the
 *                            order, no aria-current (the model portraits)
 *     [data-gallery-prev]    optional, ships with `hidden`
 *     [data-gallery-next]    optional, ships with `hidden`
 *     [data-gallery-status]  optional live region (role="status")
 *
 *   and, when the catalogue allows the zoom:
 *
 *     [data-gallery-zoom]          one per slide, in the same order, holding
 *                                  its index; ships with `hidden`
 *     [data-gallery-dialog]        the <dialog> the zoom opens
 *     [data-gallery-zoom-viewport] the scrolling frame inside it
 *     [data-gallery-zoom-image]    the single <img> the frame holds
 *     [data-gallery-zoom-close]    optional
 *     [data-gallery-zoom-prev]     optional
 *     [data-gallery-zoom-next]     optional
 *     [data-gallery-zoom-status]   optional live region (role="status")
 *     [data-gallery-zoom-loader]   optional, ships with `hidden`; carried
 *                                  while the picture of the panel travels
 *
 * Class names are free: nothing here reads one.
 */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.stack = this.querySelector('[data-gallery-stack]');
    /* Every picture of the product is in the page; only one model's group is on
       screen at a time. The two lists below are therefore the whole gallery,
       and `this.slides` and `this.thumbs` are the part of it being shown — the
       lists the rest of this file works on, because "the next picture" means
       the next one a visitor can see. */
    this.allSlides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.allThumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.groups = [...this.querySelectorAll('[data-gallery-group]')];
    this.slides = this.allSlides.filter((slide) => !slide.hidden);
    this.thumbs = this.allThumbs.filter((thumb) => !thumb.closest('[data-gallery-group]')?.hidden);
    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    this.status = this.querySelector('[data-gallery-status]');

    /* A gallery marked pending by the inline script of the snippet — arrived
       on `#w` or `#m` for another model than the one rendered — waits for
       `setGroup` to lift the mark. Should the models block never announce
       anything, the mark still goes on `load`: deferred scripts have all run
       by then, so an announcement that has not come will not come. */
    if (this.hasAttribute('data-gallery-pending')) {
      window.addEventListener('load', () => {
        if (!this.settling) this.settle();
      }, { once: true });
    }

    if (!this.stack || !this.slides.length) return;

    /* Which model is shown is the models block's decision, announced rather
       than asked for. Registered before every guard below: a group of a single
       picture still has to be switchable. */
    this.addEventListener('product:model', (event) => this.setGroup(event.detail?.name));

    /* Before the rest, and outside its guard: a product with one image has no
       carousel to drive but is still worth looking at close up. */
    this.setUpZoom();

    /* No early return on a single picture: the group shown on arrival may
       have one photograph while another model has several, and a change of
       model would then show arrows with nobody listening to them. Everything
       below is harmless with one slide, and `syncArrows` keeps the pair
       hidden until there is something to drive. The markup ships the arrows
       hidden, as they are useless without this file. */
    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));

    /* The thumbnails are fragment links, which jump. The site glides instead,
       so the jump is replaced by a smooth scroll — the browser's own, honouring
       the `scroll-margin-top` that keeps the image clear of the header, and
       instant for anyone who asked for less movement. The link keeps working
       as a link: the address bar still gets the fragment. */
    this.thumbs.forEach((thumb) => this.glideOnClick(thumb));

    /* The model portraits are fragment links too, to the first picture of the
       model they name, and they glide the same way. They are not thumbnails,
       though: there is neither one per image nor one in each position, so they
       carry their own attribute and never take aria-current. */
    this.querySelectorAll('[data-gallery-jump]').forEach((link) => this.glideOnClick(link));

    /**
     * Which slide is on screen is read from the page rather than counted, so
     * that a swipe, a trackpad, a fragment link and the arrows all land on the
     * same answer. Shrinking the root by 45% on all four sides leaves a small
     * box at the middle of the window as the test: the slide crossing it is
     * the one being looked at, whether the stack runs down the page or across
     * it.
     */
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.setCurrent(this.slides.indexOf(entry.target));
        });
      },
      { root: null, rootMargin: '-45%', threshold: 0 }
    );
    this.observe();

    this.onScroll = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.syncArrows());
    };
    this.stack.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    this.current = -1;
    this.setCurrent(0, { silent: true });
    this.syncArrows();

    /* The snapping of the mobile scroller is turned on only once the page has
       loaded (`data-gallery-snap`, read by the stylesheet). With it on from
       the start, WebKit re-snaps the scroller when the first picture arrives
       during loading and lands it on another slide, often the last one: the
       page opened on the wrong picture on iPhone (20 Oct. 2026). Pictures
       loaded later, as the finger reaches them, do not move it. This script
       is deferred and normally runs before `load`; should it run after, the
       attribute goes on at once. */
    if (document.readyState === 'complete') {
      this.stack.setAttribute('data-gallery-snap', '');
    } else {
      window.addEventListener('load', () => this.stack.setAttribute('data-gallery-snap', ''), { once: true });
    }
  }

  /**
   * Shows one model's photographs and hides the others'. A picture with no
   * group is a packshot — the cut-out of the garment on nothing — and stays
   * whatever the model, as it does on the site.
   *
   * Nothing is fetched and nothing is rebuilt: the whole gallery is in the
   * page, so a change of model is an `hidden` moved around, then the state
   * read again from what is left on screen.
   */
  setGroup(name) {
    if (!name) return;

    this.groups.forEach((element) => {
      const group = element.dataset.galleryGroup;
      element.hidden = Boolean(group) && group !== name;
    });

    this.slides = this.allSlides.filter((slide) => !slide.hidden);
    this.thumbs = this.allThumbs.filter((thumb) => !thumb.closest('[data-gallery-group]')?.hidden);
    if (this.hasAttribute('data-gallery-pending')) this.settleOnFirstPicture();
    if (!this.slides.length) return;

    /* The stack is a scroller below the breakpoint, and the pictures it held
       are gone: it has to come back to the first of the new ones, or it would
       sit past the end of a shorter group. */
    this.stack.scrollTo({ left: 0, top: 0, behavior: 'auto' });

    this.observe();
    this.current = -1;
    this.setCurrent(0, { silent: true });
    this.syncArrows();
  }

  /**
   * Lifts the pending mark once the first picture of the new group is there.
   * Those pictures are lazy and were hidden, so they have only just started to
   * travel: the first one is asked for at once, and the grey stays until it
   * has arrived — or failed, a broken picture being no reason to keep the whole
   * gallery grey. The following ones load as they are reached, as always.
   */
  settleOnFirstPicture() {
    this.settling = true;

    const image = this.slides[0]?.querySelector('img');
    if (!image || (image.complete && image.naturalWidth > 0)) {
      this.settle();
      return;
    }

    image.loading = 'eager';
    image.addEventListener('load', () => this.settle(), { once: true });
    image.addEventListener('error', () => this.settle(), { once: true });
  }

  /** Shows the pictures again, at once, as the rest of the theme does. */
  settle() {
    this.removeAttribute('data-gallery-pending');
    this.removeAttribute('aria-busy');
  }

  /** Watches the pictures on screen, and only those. */
  observe() {
    if (!this.observer) return;

    this.observer.disconnect();
    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  /**
   * A fragment link that glides instead of jumping, the browser's own smooth
   * scroll honouring the `scroll-margin-top` that keeps the picture clear of
   * the header, and instant for anyone who asked for less movement. The link
   * keeps working as a link: the address bar still gets the fragment.
   */
  glideOnClick(link) {
    link.addEventListener('click', (event) => {
      const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (!target) return;

      event.preventDefault();
      const motionless = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: motionless ? 'auto' : 'smooth', block: 'start', inline: 'start' });
      history.replaceState(null, '', link.hash);
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.stack?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    cancelAnimationFrame(this.frame);
  }

  /**
   * Marks the thumbnail of the image on screen and says which one it is. The
   * first call is silent: announcing the image the page opens on would talk
   * over everything else being read at load.
   */
  setCurrent(index, options) {
    if (index < 0 || index === this.current) return;
    this.current = index;

    this.thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    if (this.status && !(options && options.silent)) {
      this.status.textContent = this.slides[index].dataset.galleryLabel || '';
    }
  }

  /**
   * Moves the mobile scroller one image forwards or back.
   *
   * Where it starts from is read from the scroll position rather than from the
   * index the observer holds: that index only changes once the new image has
   * crossed the middle of the window, so two quick clicks on the same arrow
   * would both compute from the image being left behind and ask twice for the
   * same one. Same reading as `image-slider`, one image at a time instead of
   * two.
   */
  step(direction) {
    const starts = this.slides.map((slide) => slide.offsetLeft);
    const from = this.stack.scrollLeft;

    let index = 0;
    starts.forEach((start, i) => {
      if (start <= from + 1) index = i;
    });

    /* The carousel loops, as on the original: back from the first picture is
       the last one, forwards from the last is the first. */
    const wanted = (index + direction + starts.length) % starts.length;
    const target = starts[wanted];

    if (target === undefined || target === from) return;

    const motionless = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.stack.scrollTo({ left: target, behavior: motionless ? 'auto' : 'smooth' });
  }

  /**
   * The carousel loops, so an arrow always has somewhere to go and is never
   * disabled — the original keeps both black on every picture (1 October 2026;
   * they were greyed at either end until then). With a single picture there
   * is nowhere to go at all, and the pair is hidden.
   */
  syncArrows() {
    const alone = this.slides.length < 2;

    [this.prev, this.next].forEach((arrow) => {
      if (!arrow) return;
      arrow.hidden = alone;
      arrow.disabled = false;
    });
  }

  /**
   * The zoom: the picture alone, over the window, at the width of the screen
   * instead of the width of its column.
   *
   * The openers ship hidden for the same reason as the arrows — without this
   * file there is no zoom, and a control that leads nowhere is worse than no
   * control. The panel is a native modal `<dialog>`, as everywhere else in
   * this theme: Escape, the veil, the focus trap and the return of the focus
   * to the picture one came from are the browser's work, not ours. The
   * original puts a plain `div` in `position: fixed`, so its page stays
   * tabbable behind the veil and its zoom cannot be opened from a keyboard
   * at all.
   */
  setUpZoom() {
    this.dialog = this.querySelector('[data-gallery-dialog]');
    this.zoomImage = this.querySelector('[data-gallery-zoom-image]');
    this.zoomFrame = this.querySelector('[data-gallery-zoom-viewport]');
    this.zoomStatus = this.querySelector('[data-gallery-zoom-status]');
    this.zoomLoader = this.querySelector('[data-gallery-zoom-loader]');
    this.openers = [...this.querySelectorAll('[data-gallery-zoom]')];

    if (!this.dialog || !this.zoomImage || !this.openers.length) return;

    /* Bound once for good rather than per picture: stepping from one to the
       next while the first is still travelling would otherwise leave a
       listener behind that turns the loader off for the wrong picture. */
    this.zoomImage.addEventListener('load', () => this.setZoomLoading(false));
    this.zoomImage.addEventListener('error', () => this.setZoomLoading(false));

    /* Which picture an opener belongs to is looked up in the DOM rather than
       carried as a number: the openers of the hidden models are in the page
       too, so a position written by the server stopped matching the pictures
       on screen as soon as a group could be hidden. */
    this.openers.forEach((opener) => {
      opener.hidden = false;
      opener.addEventListener('click', () => {
        const slide = opener.closest('[data-gallery-slide]');
        this.dialog.showModal();
        this.showZoom(this.slides.indexOf(slide));
      });
    });

    this.querySelector('[data-gallery-zoom-close]')?.addEventListener('click', () => this.dialog.close());
    this.querySelector('[data-gallery-zoom-prev]')?.addEventListener('click', () => this.stepZoom(-1));
    this.querySelector('[data-gallery-zoom-next]')?.addEventListener('click', () => this.stepZoom(1));

    /* Left and right walk the pictures, as on the original, which listens on
       the window; here the listener belongs to the panel, so nothing is left
       behind once it closes. */
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.stepZoom(event.key === 'ArrowLeft' ? -1 : 1);
    });

    /* Clicking beside the picture closes, as it does on the tab panel. A click
       on the panel itself can only be one that missed the frame or a button. */
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
  }

  /**
   * Shows one picture, taken from the slide it belongs to rather than rendered
   * a second time: the two then always agree, and the page carries one set of
   * sources instead of two. The `sizes` of the panel differ from the stack's,
   * so the browser fetches the candidate this width deserves.
   *
   * The frame is the grey the theme puts under every image that has not
   * arrived, and the loader of the original turns over it meanwhile — the
   * source of the panel is a picture of two thousand pixels, a wait worth
   * showing.
   */
  showZoom(index) {
    const total = this.slides.length;

    /* The ends join, as on the original: after the last comes the first. */
    this.zoomed = ((index % total) + total) % total;

    const source = this.slides[this.zoomed].querySelector('img');
    if (source) {
      this.setZoomLoading(true);
      this.zoomImage.alt = source.alt;
      this.zoomImage.srcset = source.srcset || '';
      this.zoomImage.src = source.getAttribute('src') || '';

      /* Its shape, so the frame knows how much room to keep before the larger
         source lands. Read from the attributes the slide carries, not from the
         element, whose `width` would be the size it happens to be drawn at. */
      ['width', 'height'].forEach((side) => {
        const value = source.getAttribute(side);
        if (value) this.zoomImage.setAttribute(side, value);
      });

      /* A picture already held by the browser never fires `load` again. */
      if (this.zoomImage.complete && this.zoomImage.naturalWidth > 0) this.setZoomLoading(false);
    }

    if (this.zoomStatus) {
      this.zoomStatus.textContent = this.slides[this.zoomed].dataset.galleryLabel || '';
    }

    this.placeZoom();
  }

  /** Carries the loader of the panel, when the markup holds one. */
  setZoomLoading(state) {
    if (this.zoomLoader) this.zoomLoader.hidden = !state;
  }

  /** One picture forwards or back inside the panel. */
  stepZoom(direction) {
    if (this.zoomed === undefined) return;
    this.showZoom(this.zoomed + direction);
  }

  /**
   * Where the picture sits when it appears.
   *
   * Below the breakpoint it is twice the width of the frame, so it opens at
   * its middle and can be walked in both directions — the gesture of the
   * original. Above it, it is exactly as wide as the frame and opens at the
   * top, which is where the original leaves it.
   *
   * Done twice: once now, for the width, which the frame already knows, and
   * once when the picture lands, for the height, which nothing knows before.
   */
  placeZoom() {
    const frame = this.zoomFrame;
    if (!frame) return;

    const place = () => {
      const narrow = window.matchMedia('(max-width: 900px)').matches;
      frame.scrollLeft = narrow ? (frame.scrollWidth - frame.clientWidth) / 2 : 0;
      frame.scrollTop = narrow ? (frame.scrollHeight - frame.clientHeight) / 2 : 0;
    };

    place();
    if (!this.zoomImage.complete) this.zoomImage.addEventListener('load', place, { once: true });
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
